import React from 'react';
import { Box, Stack, Typography, Skeleton } from '@mui/material';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import ProficiencyDots from 'shared/components/proficiencyDots';
import { colors } from 'styles/colors';
import ProficiencyDotsSkeleton from 'shared/components/proficiencyDotsSkeleton';
import { ICandidateInterviewFeedbackOverallRating } from 'modules/hiring/models/candidateFeedbackOverallRating.model';
import { formatToOneDecimal } from 'modules/hiring/utilities';

export interface ICandidateInterviewFeedbackOverallRatingProps {
  aggregateData: ICandidateInterviewFeedbackOverallRating | null;
  loading: boolean;
}

const CandidateInterviewFeedbackOverallRatingSkeleton = () => {
  return (
    <Stack spacing={2} data-testid='feedback-overall-rating-shimmer'>
      <Skeleton
        variant='rectangular'
        width='7.125rem'
        height={24}
        sx={{
          bgcolor: (theme) => theme.palette.neutral?.main,
        }}
      />
      <Skeleton
        variant='rectangular'
        width='7.125rem'
        height={52}
        sx={{
          bgcolor: (theme) => theme.palette.neutral?.main,
        }}
      />
      <ProficiencyDotsSkeleton />
      <Skeleton
        variant='rectangular'
        width='7.125rem'
        height={20}
        sx={{
          bgcolor: (theme) => theme.palette.neutral?.main,
        }}
      />
    </Stack>
  );
};

const CandidateInterviewFeedbackOverallRating = (
  props: ICandidateInterviewFeedbackOverallRatingProps
) => {
  const { aggregateData, loading } = props;

  return (
    <Box
      sx={{
        p: '1.25rem',
        minWidth: '9.875rem',
        borderRadius: '1rem',
      }}
      id='candidate-feedback-overall-rating'
      data-testid='candidate-feedback-overall-rating'
    >
      {loading ? (
        <CandidateInterviewFeedbackOverallRatingSkeleton />
      ) : (
        <Stack spacing={2}>
          <TypographyWithI18N
            i18nKey='overallRating'
            className='kfic2-bodyText'
            id='overall-rating'
            data-testid='overall-rating'
            sx={{ textAlign: 'center' }}
          />
          <Typography
            className='kfic2-heroBold'
            id='overall-rating-count'
            data-testid='overall-rating-count'
            sx={{ textAlign: 'center' }}
          >
            {formatToOneDecimal(aggregateData?.overallScore!)}
          </Typography>
          <ProficiencyDots
            count={5}
            rating={aggregateData?.overallScore}
            size='1.125rem'
          />
          <TypographyWithI18N
            i18nKey='someInterviewRatings'
            id='no-of-interviewers'
            data-testid='no-of-interviewers'
            className='kfic2-bodyTextSmall'
            options={{ arg1: aggregateData?.interviewFeedbacksReceived }}
          />
        </Stack>
      )}
    </Box>
  );
};

export default CandidateInterviewFeedbackOverallRating;
